import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { testimonials } from '@/mocks/home';

export default function About() {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);
    const honeypot = formData.get('company_alt')?.toString().trim() ?? '';
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pkbbmb4qjhggaepvtg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }
      const code = (parsed as { code?: string }).code;
      const metaMsg = (parsed as { meta?: { message?: string } }).meta?.message;
      const serverMsg = (parsed as { message?: string }).message ?? metaMsg ?? responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        formEl.reset();
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '发送失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查您的连接后重试。');
    }
  };

  return (
    <main>
      <Navbar />
      <div className="pt-16 md:pt-18">
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">{t('breadcrumb.home')}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-600">{t('nav.about')}</span>
            </nav>

            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 mb-16 md:mb-20">
              <div className="w-full lg:w-1/2">
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">
                  {t('about.story_title')}
                </h1>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-4">
                  91豆花的故事始于2015年，创始人怀着对传统美食的热爱，走访了全国各地的豆花老店，学习最地道的制作工艺。从四川的麻辣豆花到广东的甜豆花，从江南的精致到北方的豪爽，我们用心记录每一种味道背后的故事。
                </p>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-4">
                  经过数年的沉淀与积累，91豆花于2020年在成都开设了第一家直营门店。我们坚持使用非转基因黄豆，传承古法石磨工艺，以最纯粹的方式呈现豆花的本真味道。每一碗91豆花，都是对千年豆花文化的致敬。
                </p>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                  如今，91豆花已发展成为一个集传统豆花制作、创新口味研发、豆花文化传播于一体的综合性美食品牌。我们不仅为消费者提供美味的豆花产品，更致力于让更多人了解和爱上这份独特的中国传统美食。
                </p>
              </div>
              <div className="w-full lg:w-1/2">
                <div className="relative w-full h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20and%20inviting%20traditional%20Chinese%20tofu%20pudding%20douhua%20shop%20interior%20with%20wooden%20furnishings%2C%20showcasing%20artisanal%20food%20preparation%2C%20steaming%20bowls%20of%20fresh%20tofu%20pudding%2C%20warm%20ambient%20lighting%2C%20clean%20and%20modern%20yet%20traditional%20aesthetic%2C%20cozy%20atmosphere&width=800&height=600&seq=about-story-01&orientation=landscape"
                    alt="91豆花品牌故事"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="mb-16 md:mb-20">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
                {t('about.mission_title')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                <div className="p-6 md:p-8 rounded-lg bg-background-100/70 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-seedling-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">品质为本</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">从大豆选料到制作工艺，我们严格把控每一个环节，只为呈现最优质的豆花产品。</p>
                </div>
                <div className="p-6 md:p-8 rounded-lg bg-background-100/70 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-lightbulb-flash-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">创新融合</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">在传承经典的基础上不断创新，将豆花与现代美食理念融合，创造更多可能。</p>
                </div>
                <div className="p-6 md:p-8 rounded-lg bg-background-100/70 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                    <i className="ri-heart-line text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">文化传承</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">致力于传播豆花美食文化，让更多人了解并爱上这份千年的传统美味。</p>
                </div>
              </div>
            </div>

            <div className="mb-16 md:mb-20">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
                {t('testimonials.title')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {testimonials.map((item, idx) => (
                  <div key={idx} className="p-6 md:p-8 rounded-lg bg-background-100/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`ri-star-fill text-sm ${i < item.rating ? 'text-primary-500' : 'text-background-300'}`}></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-5 italic">&ldquo;{item.comment}&rdquo;</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                        <img src={item.avatar} alt={item.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                        <p className="text-xs text-foreground-400">{item.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <section id="contact">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-8 text-center">
                {t('about.contact_title')}
              </h2>
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 max-w-5xl mx-auto">
                <div className="w-full lg:w-2/5">
                  <div className="flex flex-col gap-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-base font-semibold text-foreground-900 mb-1">{t('footer.phone')}</h4>
                        <p className="text-sm text-foreground-500">400-910-0000</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-base font-semibold text-foreground-900 mb-1">{t('footer.email')}</h4>
                        <p className="text-sm text-foreground-500">contact@91douhua.com</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <h4 className="font-heading text-base font-semibold text-foreground-900 mb-1">{t('footer.address')}</h4>
                        <p className="text-sm text-foreground-500">中国四川省成都市锦江区东大街168号</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-full lg:w-3/5">
                  {formStatus === 'success' ? (
                    <div className="py-10 px-6 rounded-lg bg-secondary-100 text-secondary-800 text-center">
                      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-200">
                        <i className="ri-check-line text-2xl text-secondary-600"></i>
                      </div>
                      <p className="text-base font-medium">{t('about.form_success')}</p>
                    </div>
                  ) : (
                    <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="" className="flex flex-col gap-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <input
                            type="text"
                            name="name"
                            required
                            placeholder={t('about.form_name')}
                            className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            required
                            placeholder={t('about.form_email')}
                            className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                          />
                        </div>
                      </div>
                      <div>
                        <input
                          type="tel"
                          name="phone"
                          placeholder={t('about.form_phone')}
                          className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          required
                          rows={4}
                          maxLength={500}
                          placeholder={t('about.form_message')}
                          className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      <button
                        type="submit"
                        className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        {t('about.form_submit')}
                      </button>
                    </form>
                  )}
                  {formStatus === 'error' && (
                    <p className="mt-4 text-sm text-accent-600 text-center">{formError}</p>
                  )}
                </div>
              </div>
            </section>
          </div>
        </section>
      </div>
      <Footer />
    </main>
  );
}